<template>
  <div class="content">
    <template v-if="applications.length">
      <template v-for="application in applications" :key="application.id">
        <h2>
          Заявление
          <router-link
            :to="{
              name: 'secretary.application',
              params: { formId: application.form_id, applicationId: application.id, section: 'overview' },
            }"
            v-if="readonly"
          >
            №{{ application.sspvo_unique_code }} ({{ application.education_level }})
          </router-link>
          <router-link
            :to="{
              name: 'abiturient.application',
              params: { id: application.id, section: 'overview' },
            }"
            v-else
          >
            №{{ application.sspvo_unique_code }} ({{ application.education_level }})
          </router-link>
        </h2>

        <template v-for="section in sectionsFor(application)" :key="section.name">
          <h3>{{ section.name }}</h3>
          <div class="form__input">
            <ol class="list">
              <li class="list__item" v-for="group in section.groups" :key="group.priority" :value="group.priority">
                <template v-for="item in group.items" :key="item.id">
                  <div class="card">
                <div class="card__header">
                  <h5 class="join join--col join--indent-sm">
                    <span>
                      <a v-if="item.url" :href="item.url" target="_blank">{{ item.name }}</a>
                      <span v-else>{{ item.name }}</span>
                    </span>
                    <span class="join join--wrap join--indent-sm">
                      <span
                        v-for="badge in badges(item)"
                        :key="badge.text"
                        class="badge badge--pill"
                        :class="badge.class"
                      >
                        {{ badge.text }}
                      </span>
                    </span>
                  </h5>
                </div>
              </div>
                  <ul class="list">
                    <li class="list__item list__item--xs card" v-for="profile in item.profiles" :key="profile.id">
                      <small>{{ profile.name }}</small>
                    </li>
                  </ul>
                </template>
              </li>
            </ol>
          </div>
        </template>
      </template>
    </template>

    <div class="card card--info" v-else>
      <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
      <p>Заявления не найдены</p>
    </div>
  </div>
</template>

<script setup>
defineProps({
  applications: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
})

const allItems = (application) => application.rating_lists?.flatMap(rl => rl.items) ?? []

const budgetItems = (application) => {
  const list = application.rating_lists?.find(rl => rl.kind === 'budget')
  return (list?.items ?? []).filter(item => item.status !== 'Отозвано')
}

const payItems = (application) => {
  const list = application.rating_lists?.find(rl => rl.kind === 'pay')
  return (list?.items ?? []).filter(item => item.status !== 'Отозвано')
}

const withdrawnItems = (application) =>
  allItems(application).filter(item => item.status === 'Отозвано')

const groupedItemsByRules = (items) => {
  const groups = {}

  items.forEach(item => {
    groups[item.priority] ??= { priority: item.priority, items: [] }
    groups[item.priority].items.push(item)
  })

  const arrayGroups = Object.values(groups)

  const weight = (item) => {
    if (item.reception_feature === 'Отдельная квота') return 1
    if (item.reception_feature === 'Общие места' && item.benefits_only) return 2
    if (item.reception_feature === 'Общие места') return 3
    return 4
  }

  arrayGroups.forEach(group => {
    group.items.sort((a, b) => weight(a) - weight(b))
  })

  return arrayGroups
}

const sectionsFor = (application) => [
  { name: 'Бюджетные направления подготовки/специальности', groups: groupedItemsByRules(budgetItems(application)) },
  { name: 'Платные направления подготовки/специальности', groups: groupedItemsByRules(payItems(application)) },
  { name: 'Отозванные направления подготовки/специальности', groups: groupedItemsByRules(withdrawnItems(application)) },
].filter(section => section.groups.length)

const badges = (item) => {
  const result = []
  if (item.status) result.push({ text: `Статус: ${item.status}`, class: statusBadgeClass(item) })

  if (item.priorities?.is_highest_priority) result.push({ text: 'Основной высший приоритет', class: 'badge--solid-success' })
  if (item.priorities?.is_highest_passing_priority) result.push({ text: 'Высший проходной приоритет', class: 'badge--solid-success' })
  if (item.without_exam) result.push({ text: 'Без вступительных испытаний', class: 'badge--solid-success' })

  if (item.is_combined_quota) result.push({ text: 'Совмещённая квота', class: 'badge--solid-primary' })
  if (item.benefits_only) result.push({ text: 'Льгота', class: 'badge--solid-primary' })

  if (item.only_russian) result.push({ text: 'Для граждан РФ', class: 'badge--solid-muted' })
  if (item.is_spo) result.push({ text: 'СПО', class: 'badge--solid-muted' })
  if (item.is_vo) result.push({ text: 'ВО', class: 'badge--solid-muted' })

  return result
}

const statusBadgeClass = (item) => item.status === 'Отозвано' ? 'badge--status-danger' : 'badge--status'
</script>
